"use client";

import { c as _c } from "react/compiler-runtime";
import { useCallback, useEffect, useRef, useState, useTransition } from "react";
import { useRouter } from "next/navigation";
import { analyzeProfile } from "../_lib/actions";
import type { AnalyzeResult, ProfileStatus } from "../_lib/types";

/**
 * COLD-state island for /instagram/<username>.
 *
 * A bare GET costs $0: the server renders this shell, no API call. The paid
 * fetch only fires on explicit intent. We auto-fire `analyzeProfile` when the
 * visitor arrived from our own funnel (same-origin referrer OR a sessionStorage
 * hand-off flag) so the primary funnel feels instant; a cold deep-link sees a
 * single on-brand CTA instead.
 *
 * On status 'ok' we router.refresh() — the now-warm RSC re-renders the full
 * real page. Every other outcome is honest: throttled / unavailable. We NEVER
 * render fabricated metrics while loading or on failure.
 */

type UiState = "idle" | "loading" | {
  error: Exclude<ProfileStatus, "ok">;
};
const FIRST_PARTY_FLAG = "outfame:analyze-intent";
function isFirstPartyIntent(): boolean {
  if (typeof window === "undefined") return false;
  try {
    // explicit hand-off from a first-party submit (hero/card → push to this URL)
    if (sessionStorage.getItem(FIRST_PARTY_FLAG)) {
      sessionStorage.removeItem(FIRST_PARTY_FLAG);
      return true;
    }
  } catch {
    // sessionStorage can throw in privacy modes — fall through to referrer
  }
  const ref = document.referrer;
  if (!ref) return false;
  try {
    return new URL(ref).origin === window.location.origin;
  } catch {
    return false;
  }
}
export function AnalyzeState({
  username
}: {
  username: string;
}) {
  const router = useRouter();
  const [state, setState] = useState<UiState>("idle");
  const [isPending, startTransition] = useTransition();
  const firedRef = useRef(false);
  const run = useCallback(() => {
    if (firedRef.current) return;
    firedRef.current = true;
    setState("loading");
    startTransition(async () => {
      let result: AnalyzeResult;
      try {
        result = await analyzeProfile(username);
      } catch {
        // Server action should never throw, but never leave a dead spinner.
        firedRef.current = false;
        setState({
          error: "degraded"
        });
        return;
      }
      if (result.status === "ok") {
        // warm now — re-render the RSC with real data in place
        router.refresh();
        return; // keep the loader up through the refresh; no flash back to CTA
      }
      firedRef.current = false;
      setState({
        error: result.status
      });
    });
  }, [username, router]);

  // Auto-fire only for first-party intent. Cold deep-links wait for the click.
  useEffect(() => {
    if (isFirstPartyIntent()) run();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const loading = state === "loading" || isPending;
  return <section aria-labelledby="analyze-heading" className="mx-auto max-w-2xl pb-16 pt-[112px] text-center sm:pb-24 sm:pt-[132px]">
            <span className="mb-6 inline-flex items-center rounded-full bg-[#fdf0f7] px-3.5 py-1.5 text-[12px] font-bold uppercase tracking-[0.1em] text-[#be1d6c] ring-1 ring-[#f7cfe5]">Instagram profile analysis</span>

            <h1 id="analyze-heading" className="text-3xl font-bold tracking-tight text-[#141414] [text-wrap:balance] sm:text-[2.5rem] sm:leading-[1.1]">
                <span>@{username}</span>
            </h1>

            {loading ? <LoadingState username={username} /> : typeof state === "object" ? <ErrorState username={username} status={state.error} onRetry={run} /> : <IdleState username={username} onAnalyze={run} />}
        </section>;
}
function IdleState(t0) {
  const $ = _c(10);
  const {
    username,
    onAnalyze
  } = t0;
  let t1;
  if ($[0] !== username) {
    t1 = <p className="mx-auto mt-4 max-w-md text-[17px] leading-7 text-gray-600 [text-wrap:pretty]">See @{username}'s real engagement rate, average likes and comments, posting rhythm, and top posts — computed from their most recent activity.</p>;
    $[0] = username;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <svg viewBox="0 0 20 20" fill="none" aria-hidden="true" className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-1"><path d="M4 10h12m0 0l-5-5m5 5l-5 5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" /></svg>;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] !== onAnalyze || $[4] !== username) {
    t3 = <button type="button" onClick={onAnalyze} className="group mt-8 inline-flex h-12 items-center justify-center gap-2 rounded-full bg-[rgb(255,163,209)] px-7 text-[15px] font-semibold text-ink transition-colors duration-200 hover:bg-[#fc83bf] hover:shadow-lg hover:shadow-pink-500/20 focus:outline-none focus-visible:ring-2 focus-visible:ring-[rgb(255,163,209)] focus-visible:ring-offset-2">Analyze @{username}'s Instagram{t2}</button>;
    $[3] = onAnalyze;
    $[4] = username;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  let t4;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = <p className="mt-4 text-[13px] text-gray-500">Real data · no login · takes a few seconds</p>;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  let t5;
  if ($[7] !== t1 || $[8] !== t3) {
    t5 = <div className="animate-fade-in-gentle">{t1}{t3}{t4}</div>;
    $[7] = t1;
    $[8] = t3;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  return t5;
}
function LoadingState(t0) {
  const $ = _c(7);
  const {
    username
  } = t0;
  let t1;
  if ($[0] !== username) {
    t1 = <p className="mt-4 text-[15px] font-medium text-gray-500">Analyzing @{username}…</p>;
    $[0] = username;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <div className="grid grid-cols-1 gap-3.5 sm:grid-cols-2"><SkeletonCard tall={true} /><SkeletonCard tall={true} /></div>;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = <div className="grid grid-cols-1 gap-3.5 sm:grid-cols-3"><SkeletonCard /><SkeletonCard /><SkeletonCard /></div>;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  let t4;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = <div className="mt-10 space-y-3.5 text-left">{t2}{t3}<div className="grid grid-cols-3 gap-2.5 pt-2 sm:grid-cols-4">{Array.from({
          length: 8
        }).map(_temp)}</div></div>;
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  let t5;
  if ($[5] !== t1) {
    t5 = <div className="animate-fade-in-gentle" aria-live="polite" aria-busy="true">{t1}{t4}</div>;
    $[5] = t1;
    $[6] = t5;
  } else {
    t5 = $[6];
  }
  return t5;
}
function _temp(_, i) {
  return <div key={i} className="aspect-square animate-pulse rounded-xl bg-gray-100" />;
}
function SkeletonCard(t0) {
  const $ = _c(7);
  const {
    tall
  } = t0;
  const t1 = `rounded-2xl border border-gray-200/70 bg-white ${tall ? "p-6 sm:p-7" : "p-5"}`;
  let t2;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <div className="h-3 w-24 animate-pulse rounded bg-gray-100" />;
    $[0] = t2;
  } else {
    t2 = $[0];
  }
  const t3 = `mt-4 animate-pulse rounded bg-gray-100 ${tall ? "h-10 w-32" : "h-7 w-20"}`;
  let t4;
  if ($[1] !== t3) {
    t4 = <div className={t3} />;
    $[1] = t3;
    $[2] = t4;
  } else {
    t4 = $[2];
  }
  let t5;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = <div className="mt-3 h-3 w-full animate-pulse rounded bg-gray-100" />;
    $[3] = t5;
  } else {
    t5 = $[3];
  }
  let t6;
  if ($[4] !== t1 || $[5] !== t4) {
    t6 = <div className={t1}>{t2}{t4}{t5}</div>;
    $[4] = t1;
    $[5] = t4;
    $[6] = t6;
  } else {
    t6 = $[6];
  }
  return t6;
}
function ErrorState(t0) {
  const $ = _c(11);
  const {
    username,
    status,
    onRetry
  } = t0;
  const throttled = status === "throttled";
  const headline = throttled ? "We're at capacity right now" : `We couldn't analyze @${username}`;
  const body = throttled ? "A lot of profiles are being analyzed at once. Give it a moment and try again." : "This account is private or unavailable, so there's nothing real for us to show. Double-check the handle and try another.";
  let t1;
  if ($[0] !== headline) {
    t1 = <p className="mx-auto mt-5 max-w-md text-[17px] font-semibold text-[#141414]">{headline}</p>;
    $[0] = headline;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== body) {
    t2 = <p className="mx-auto mt-2 max-w-md text-[15px] leading-7 text-gray-600 [text-wrap:pretty]">{body}</p>;
    $[2] = body;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== onRetry || $[5] !== throttled) {
    t3 = throttled && <button type="button" onClick={onRetry} className="mt-7 inline-flex h-11 items-center justify-center rounded-full border border-gray-300 bg-white px-6 text-[14px] font-semibold text-[#141414] transition-colors duration-200 hover:bg-gray-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-[rgb(255,163,209)] focus-visible:ring-offset-2">Try again</button>;
    $[4] = onRetry;
    $[5] = throttled;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  let t4;
  if ($[7] !== t1 || $[8] !== t2 || $[9] !== t3) {
    t4 = <div className="animate-fade-in-gentle">{t1}{t2}{t3}</div>;
    $[7] = t1;
    $[8] = t2;
    $[9] = t3;
    $[10] = t4;
  } else {
    t4 = $[10];
  }
  return t4;
}